// frontend/src/services/rosterMatchService.js
// Fetches Sportradar-mirror candidates for a parsed roster.
//
// Reads only. Nothing in this file writes to Sportradar or to the mirror.

import { supabase } from './supabaseCore';
import { searchSportradar } from './sportradarService';
import { normalizeName } from '../utils/matchRoster';

// Columns the review screen and scorer need. raw_data is deliberately excluded: it is the
// bulk of the row and Sportradar leaves the parts we would have wanted from it null.
const CANDIDATE_COLUMNS = 'person_id, name_full_latin, name_full_local, nationality, dob, gender, status, current_photo_url';

// Every token costs a scan of the whole table, and short ones earn nothing. Matching is
// by substring, so "van" also drags in Sullivan and Donovan — 680 rows for a fragment
// that distinguishes nobody.
const MIN_TOKEN = 4;
const TOKENS_PER_QUERY = 12;
const MAX_CANDIDATES = 2000;

// Nobody is identified by their nameplate particles. Dutch, Portuguese, Arabic and
// Spanish names carry several each, and a squad's worth of them dominates the search.
const PARTICLES = new Set([
    'van', 'von', 'der', 'den', 'des', 'del', 'dela', 'della', 'delle', 'dos', 'das',
    'bin', 'ibn', 'abu', 'ben', 'the', 'mac', 'saint',
]);

/**
 * Distinct searchable tokens across every name form in the roster.
 *
 * A person only needs one of their tokens to be searched for the row to be fetched;
 * scoring then happens on the full name client-side. So the aim is the smallest set of
 * distinctive tokens, not completeness.
 *
 * A name made entirely of short tokens ("Li Wei") keeps its longest one regardless,
 * otherwise that person would be unsearchable rather than merely expensive.
 */
export const searchTokensFor = (entries) => {
    const tokens = new Set();

    for (const entry of entries) {
        const names = entry.nameCandidates?.length ? entry.nameCandidates : [entry.fullName];
        const own = new Set();
        for (const name of names) {
            for (const token of normalizeName(name).split(' ')) {
                if (token && !PARTICLES.has(token)) own.add(token);
            }
        }

        const distinctive = [...own].filter((t) => t.length >= MIN_TOKEN);
        if (distinctive.length) {
            distinctive.forEach((t) => tokens.add(t));
        } else {
            const longest = [...own].sort((a, b) => b.length - a.length)[0];
            if (longest) tokens.add(longest);
        }
    }

    return [...tokens];
};

const chunk = (items, size) => {
    const out = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
};

/**
 * Mirror rows that share a name token with anyone on the roster.
 *
 * Searched by name rather than by nationality on purpose. Sportradar splits one country
 * across IOC and ISO3 codes, and tracks `representing` separately from `nationality`, so
 * a nationality filter silently drops people who are present. Name tokens do not care
 * which bucket a person landed in.
 *
 * RLS restricts this table to super admins, so a lesser role simply gets nothing back
 * and the review screen shows every row as unmatched.
 */
export const fetchCandidates = async (entries) => {
    const tokens = searchTokensFor(entries);
    if (!tokens.length) return { candidates: [], error: null };

    try {
        const batches = await Promise.all(
            chunk(tokens, TOKENS_PER_QUERY).map((group) => {
                const filter = group
                    .flatMap((t) => [`name_full_latin.ilike.*${t}*`, `name_full_local.ilike.*${t}*`])
                    .join(',');
                return supabase
                    .from('sportradar_persons')
                    .select(CANDIDATE_COLUMNS)
                    .or(filter)
                    .limit(MAX_CANDIDATES);
            })
        );

        const byId = new Map();
        for (const { data, error } of batches) {
            if (error) throw error;
            for (const row of data || []) byId.set(row.person_id, row);
        }

        return { candidates: [...byId.values()], error: null };
    } catch (error) {
        console.error('Roster candidate lookup failed:', error);
        return { candidates: [], error };
    }
};

/** A live Sportradar person, reshaped to look like a mirrored row. */
export const asMirrorRow = (person) => ({
    person_id: person.personId,
    name_full_latin: person.nameFullLatin || null,
    name_full_local: person.nameFullLocal || null,
    nationality: person.nationality || null,
    dob: person.dob || null,
    gender: person.gender || null,
    status: person.status || null,
    current_photo_url: (person.images || []).find((i) => i.imageType === 'PERSON_HEAD')?.url
        || (person.images || [])[0]?.url
        || null,
    fromLiveSearch: true,
});

// The mirror is refreshed on the 1st and 15th, so it can be a fortnight behind. Anyone
// registered since is missing from it — and someone entered into Sportradar in the last
// few days is very likely being registered for the event being imported right now. Live
// search covers exactly that gap, and only for the handful of rows the mirror missed.
const LIVE_SEARCH_CONCURRENCY = 4;

/**
 * Search Sportradar directly for entries the mirror could not place.
 *
 * Returns a person array in mirror shape, for re-scoring alongside the mirror hits.
 * Failures are swallowed per entry: a live search that errors should leave the row
 * unmatched, not abandon the whole import.
 */
export const liveSearchFor = async (entries, { onProgress } = {}) => {
    const found = new Map();
    const queue = entries.filter((e) => e.fullName);
    let done = 0;

    const worker = async () => {
        for (;;) {
            const entry = queue.shift();
            if (!entry) return;
            try {
                const { data } = await searchSportradar(entry.fullName);
                for (const person of data || []) {
                    if (person?.personId) found.set(person.personId, asMirrorRow(person));
                }
            } catch {
                // Leave this row unmatched rather than failing the import.
            }
            onProgress?.(++done, entries.length);
        }
    };

    await Promise.all(Array.from({ length: Math.min(LIVE_SEARCH_CONCURRENCY, queue.length) }, worker));
    return [...found.values()];
};
